/**
 * לוח הבקרה של הפאנל — TASKS 3.1–3.2, 6.3ב · DESIGN.md 8.2.
 *
 * ⭐ שלב 3 (3.1–3.6) הושלם: קטגוריות · מוצרים · מדיה · יומן פעולות. ⛔ אין
 *    מסך הגדרות — הוסר במכוון (11.08.2026, ראו TASKS 3.6).
 *
 * ⚠️ **סטייה מתועדת משורת ה-stat cards המילולית ב-8.2:** המפרט מבקש "בקשות
 *    חדשות" ו-"הצעות פתוחות" — שני מושגים שאין להם שדה תואם בפועל (הצעות
 *    מגיעות בלי `status`, אין ישות "בקשה" נפרדת מהצעה). הוחלף בשני מספרים
 *    אמיתיים וזמינים: הצעות החודש (`listQuotes` עם `dateFrom`) ולקוחות
 *    קבועים (`listCustomers` עם `isRegular`) — ⛔ לא המצאת שדה סטטוס שלא קיים.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardText, Package, Users, Warning } from '@phosphor-icons/react';
import { AdminNav } from './AdminNav.js';
import { AdminPageHeader } from './AdminPageHeader.js';
import { StatCard } from './StatCard.js';
import { adminMe, isAdminApiError } from './adminApi.js';
import { listQuotes } from './quotesApi.js';
import { listProducts } from './productsApi.js';
import { listCustomers } from './customersApi.js';
import { listPriceAlerts } from './priceAlertsApi.js';
import { PriceAlertsPanel } from './PriceAlertsPanel.js';
import { RecentQuotesPanel } from './RecentQuotesPanel.js';

const SHORTCUTS: ReadonlyArray<{ to: string; label: string; icon: typeof Package }> = [
  { to: '/admin/products', label: 'מוצרים', icon: Package },
  { to: '/admin/customers', label: 'לקוחות', icon: Users },
  { to: '/admin/quotes', label: 'הצעות', icon: ClipboardText },
];

function startOfMonthIso(): string {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
}

export function DashboardPage() {
  const [username, setUsername] = useState<string | null>(null);
  const [quotesThisMonth, setQuotesThisMonth] = useState<number | null>(null);
  const [activeProducts, setActiveProducts] = useState<number | null>(null);
  const [regularCustomers, setRegularCustomers] = useState<number | null>(null);
  const [openPriceAlerts, setOpenPriceAlerts] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    adminMe().then((result) => {
      if (!cancelled) setUsername(result?.username ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    listQuotes({ dateFrom: startOfMonthIso() })
      .then((result) => {
        if (!cancelled) setQuotesThisMonth(result.total);
      })
      .catch(() => {});
    listProducts({ isActive: true })
      .then((result) => {
        if (!cancelled) setActiveProducts(result.total);
      })
      .catch(() => {});
    listCustomers({ isRegular: true })
      .then((result) => {
        if (!cancelled) setRegularCustomers(result.total);
      })
      .catch(() => {});
    listPriceAlerts('open')
      .then((result) => {
        if (!cancelled) setOpenPriceAlerts(result.alerts.length);
      })
      .catch((err) => {
        // ⚠️ ⛔ לא קריטי — `PriceAlertsPanel` למטה כבר מציג שגיאה אם יש אחת.
        if (!isAdminApiError(err)) {
          // אין מה לעשות עם השגיאה מעבר לזה.
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <AdminNav />
      <main className="mx-auto max-w-3xl px-6 py-16">
        <p className="border-copper text-link border-s-4 ps-3 text-sm font-semibold tracking-widest">
          פאנל ניהול
        </p>

        <div className="mt-3">
          <AdminPageHeader
            title={`שלום${username ? `, ${username}` : ''}`}
            description="קטגוריות, מוצרים, לקוחות, מדיה ויומן פעולות מוכנים לניהול."
          />
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard icon={ClipboardText} label="הצעות החודש" value={quotesThisMonth} />
          <StatCard icon={Package} label="מוצרים פעילים" value={activeProducts} />
          <StatCard icon={Users} label="לקוחות קבועים" value={regularCustomers} />
          <StatCard icon={Warning} label="התראות מחיר" value={openPriceAlerts} />
        </div>

        {/* ⭐ DESIGN.md 8.2 — "קיצורי דרך: כרטיסים לחיצים למסכים תכופים". */}
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {SHORTCUTS.map(({ to, label, icon: IconComponent }) => (
            <Link
              key={to}
              to={to}
              className="product-card flex items-center gap-3 rounded-lg border border-line bg-canvas p-4"
            >
              <IconComponent
                size={20}
                weight="regular"
                aria-hidden="true"
                style={{ color: 'var(--color-copper)' }}
              />
              <span className="font-medium">{label}</span>
            </Link>
          ))}
        </div>

        <PriceAlertsPanel />
        <RecentQuotesPanel />
      </main>
    </>
  );
}
